import { type Dispatch, type SetStateAction, useCallback } from 'react';

import { useMutation } from '@apollo/client/react';

import { DELETE_CMA_REPORTS_COMPARABLES } from '@modules/reports/cma-reports/cmaReportsQueries';
import { updateCmaReportCache } from '@modules/reports/cma-reports/step-listing-compare/cacheUtils';
import CmaScraperCard from '@modules/reports/cma-reports/step-listing-compare/CmaScraperCard';
import type { CmaListingWithDistance } from '@modules/reports/cma-reports/step-listing-compare/types';

import { SortableListingCard } from './SortableListingCard';

type ListingCardDraggableProps = {
  cmaReportId: string;
  index: number;
  cmaListingDistance: CmaListingWithDistance;
  setListings: Dispatch<SetStateAction<CmaListingWithDistance[]>>;
};

const ListingCardDraggable = ({
  cmaReportId,
  index,
  cmaListingDistance,
  setListings,
}: ListingCardDraggableProps) => {
  const [deleteCmaReportsComparables, { loading: deleteLoading }] = useMutation(
    DELETE_CMA_REPORTS_COMPARABLES,
  );

  const handleRemove = useCallback(
    async (listingId: number) => {
      await deleteCmaReportsComparables({
        variables: {
          cma_report_id: cmaReportId ?? '',
          listing_id: listingId,
        },
        update: (cache, { data: newData }) => {
          updateCmaReportCache(cache, cmaReportId, newData);
        },
        onCompleted: () => {
          setListings(prevListings =>
            prevListings.filter(item => item.listing.id !== listingId),
          );
        },
      });
    },
    [cmaReportId, deleteCmaReportsComparables, setListings],
  );

  return (
    <SortableListingCard
      id={cmaListingDistance.id}
      index={index}
      offline={cmaListingDistance.listing?.offline_since != null}
      loading={deleteLoading}
      onRemove={() => handleRemove(cmaListingDistance.listing.id)}
    >
      <CmaScraperCard
        cmaListingDistance={cmaListingDistance}
        variant="horizontal"
        positionDateBadge="inline"
      />
    </SortableListingCard>
  );
};

export default ListingCardDraggable;
